// Curated slide-deck starting points for the "Slides" composer mode — a PURE
// data module (no React, no pg/ai deps) shared by the SPA template gallery
// (slide-template-picker) and validated by lib/__tests__/slide-templates.test.ts.
//
// Each template is a real deck STRUCTURE (a titled outline + a visual-style
// directive) that the hyperframes slideshow flow honors. The Slides mode routes
// to that flow (see composer-modes.ts) — discrete slides, fragment reveals,
// scene transitions, palette + typography direction — so every directive here
// maps to a capability the slideshow author actually has. NO DEAD KNOBS: the
// outline drives slide count, the style line uses only slideshow-real levers
// (fragment reveals, crossfade/slide/wipe transitions, palette, type scale).
//
// Picking a template does NOT auto-send: it drops `slideTemplateDraft(t)` into
// the composer as an editable draft (same discipline as branching drafts) and
// keeps Slides mode selected. PPTX export is intentionally out of scope.

/** Small enumerated layout kind — the picker renders a cheap CSS/SVG mock per kind. */
export type SlideThumbnailKind =
  | 'title' // big centered statement / cover
  | 'grid' // feature / 3-up card grid
  | 'chart' // data slide with a chart block
  | 'bullets' // outline with progressive bullets
  | 'quote' // single large quote / one-idea slide
  | 'timeline'; // horizontal phase / roadmap track

export interface SlideTemplate {
  /** Stable id (used as the picker key + the icon map key). */
  id: string;
  /** Display name. */
  name: string;
  /** One-line pitch shown in the picker. */
  description: string;
  emoji: string;
  /** Accent color (hex) for the picker card + thumbnail mock. */
  accent: string;
  /** Which mock layout the picker draws as the thumbnail. */
  thumbnail: SlideThumbnailKind;
  /**
   * Opening instruction dropped at the top of the draft. Carries {placeholders}
   * the user fills in before sending — mirrors agent-templates' brief style.
   */
  promptIntro: string;
  /** The deck's section list. Its length IS the slide count. */
  outline: readonly string[];
  /**
   * Visual-style directive. Uses only levers the hyperframes slideshow flow
   * honors: palette, type scale, fragment (progressive) reveals, and scene
   * transitions (crossfade / slide / wipe / push).
   */
  style: string;
}

export const SLIDE_TEMPLATES: readonly SlideTemplate[] = [
  {
    id: 'pitch-deck',
    name: 'Pitch deck',
    description: 'The classic investor story — problem, solution, traction, ask.',
    emoji: '🚀',
    accent: '#8B5CF6',
    thumbnail: 'title',
    promptIntro:
      'Build an investor pitch deck for {company} — {one-line description of what it does}. Confident and specific; use real numbers where I give them and leave clear placeholders where I do not.',
    outline: [
      'Cover — company name, one-line tagline, and the logo mark',
      'Problem — the pain, who feels it, why it matters now',
      'Solution — how we solve it, in one clear sentence + a visual',
      'Product — the core experience, 2–3 screens or a diagram',
      'Market — TAM / SAM / SOM sized honestly',
      'Traction — the up-and-to-the-right chart and 2–3 proof points',
      'Business model — how we make money, unit economics',
      'Competition — a positioning grid, why we win',
      'Team — founders and the unfair advantage',
      'The ask — how much we are raising and what it unlocks',
    ],
    style:
      'Bold, high-contrast dark palette with one vivid accent; large confident headlines and a tight type scale. Reveal each slide’s supporting points as fragments (one beat at a time) and move between slides with a quick crossfade or push.',
  },
  {
    id: 'product-launch',
    name: 'Product launch',
    description: 'Announce a new product or feature with a hero moment.',
    emoji: '✨',
    accent: '#EC4899',
    thumbnail: 'grid',
    promptIntro:
      'Build a product-launch deck announcing {product} — {what it is and who it is for}. It should feel like a reveal: build anticipation, then land the value.',
    outline: [
      'Hero cover — the product name and a one-line promise',
      'The problem — the old way, and why it is frustrating',
      'Meet {product} — the reveal slide, big and clean',
      'Key features — a 3-up grid of the standout capabilities',
      'How it works — three simple steps',
      'Proof — a quote, a stat, or a before/after',
      'Availability & CTA — pricing / where to get it / next step',
    ],
    style:
      'Brand-forward and energetic: a saturated accent on near-black, generous spacing, punchy headlines. Animate the feature grid cards in with a staggered entrance and use a slide or wipe transition on the reveal slide for a sense of momentum.',
  },
  {
    id: 'weekly-report',
    name: 'Weekly report',
    description: 'A crisp status update — highlights, metrics, blockers, next.',
    emoji: '🗓️',
    accent: '#22C55E',
    thumbnail: 'chart',
    promptIntro:
      'Build a weekly status deck for {team / project} covering the week of {date}. Keep it skimmable and honest — a leadership audience should get the gist in 60 seconds.',
    outline: [
      'Title — team / project and the week ending {date}',
      'Highlights — the 3 things that mattered this week',
      'Metrics — a simple chart of the key numbers vs. last week',
      'Progress by workstream — status per initiative',
      'Blockers & risks — what is stuck and what we need',
      'Next week — the top priorities and owners',
    ],
    style:
      'Clean, businesslike light palette with a calm accent; a strict grid and one chart per data slide (bar or line — labeled, not decorative). Understated transitions (a soft crossfade) so the content, not the motion, leads.',
  },
  {
    id: 'tutorial-explainer',
    name: 'Tutorial / explainer',
    description: 'Teach a concept step by step, revealing one idea at a time.',
    emoji: '🎓',
    accent: '#0EA5E9',
    thumbnail: 'bullets',
    promptIntro:
      'Build a teaching deck that explains {concept / how to do X} to {audience — e.g. beginners}. Assume no prior knowledge; each slide should earn the next.',
    outline: [
      'Title — what this teaches and who it is for',
      'What you’ll learn — the 3 takeaways up front',
      'The core idea — the concept in one plain-language slide',
      'Step by step — a numbered walkthrough, revealed one step at a time',
      'Worked example — the idea applied to something concrete',
      'Common pitfalls — the mistakes to avoid',
      'Recap — the takeaways restated',
      'Go further — where to practice or read next',
    ],
    style:
      'Friendly and legible: warm neutral palette, one accent for emphasis, comfortable body type. Lean on fragment reveals so steps and bullets appear in sequence as the presenter talks, and keep transitions simple (crossfade) so the focus stays on the lesson.',
  },
  {
    id: 'data-story',
    name: 'Data story',
    description: 'A narrative around one insight — big numbers and charts.',
    emoji: '📊',
    accent: '#F59E0B',
    thumbnail: 'chart',
    promptIntro:
      'Build a data-story deck that walks through {the insight / what the data shows}. Lead with the surprise, then earn it with the evidence. If I paste data, use it; otherwise leave labeled placeholders.',
    outline: [
      'The hook — one headline number, full-bleed',
      'Context — what we were measuring and why',
      'The trend — the primary chart that shows the shift',
      'The breakdown — a second chart that explains the driver',
      'What it means — the interpretation, in plain words',
      'So what — the decision or action it points to',
      'Sources — where the numbers came from',
    ],
    style:
      'Chart-forward editorial look: dark canvas, a single strong accent for the data, oversized numerals on the hook slide, quiet supporting text. One clearly-labeled chart per data slide; reveal chart elements progressively (axis, then series, then callout) and cut between slides with a clean crossfade.',
  },
  {
    id: 'minimal-keynote',
    name: 'Minimal keynote',
    description: 'One idea per slide, huge type, generous whitespace.',
    emoji: '⬛',
    accent: '#E5E7EB',
    thumbnail: 'quote',
    promptIntro:
      'Build a minimal, keynote-style deck about {topic / the single argument}. One idea per slide — say less, land harder. I will narrate over it.',
    outline: [
      'Opening statement — the thesis in a few big words',
      'Tension — the thing everyone assumes',
      'Turn — why that is wrong (or incomplete)',
      'The idea — the point, stated plainly and large',
      'Evidence — one concrete proof, nothing more',
      'Implication — what changes if this is true',
      'One image — a single visual that carries the feeling',
      'Restate — the thesis again, now that it means more',
      'Close — the one line to remember',
    ],
    style:
      'Stark monochrome (near-black or pure white), enormous type filling the frame, almost no chrome and lots of empty space. One line of text per slide where possible. Slow, quiet transitions — a gentle fade between ideas, never flashy.',
  },
  {
    id: 'roadmap-strategy',
    name: 'Roadmap & strategy',
    description: 'Where we are, where we’re going, and the plan to get there.',
    emoji: '🧭',
    accent: '#6366F1',
    thumbnail: 'timeline',
    promptIntro:
      'Build a strategy / roadmap deck for {team or company} for {period — e.g. H2 2026}. Ground it in where we are today, then commit to a clear plan.',
    outline: [
      'Title — the plan and the period it covers',
      'Where we are — an honest snapshot of today',
      'Vision — where we are going and why',
      'Priorities — the 3 pillars we are betting on',
      'Roadmap — the phases across the timeline',
      'Success metrics — how we will know it worked',
      'The commitment — what we are asking for and owning',
    ],
    style:
      'Structured and steady: an indigo-leaning palette, a strong grid, and a horizontal timeline track on the roadmap slide with phases revealed left to right. Use a slide/push transition to reinforce forward motion through the plan.',
  },
] as const;

export function getSlideTemplate(id: string): SlideTemplate | undefined {
  return SLIDE_TEMPLATES.find((t) => t.id === id);
}

/**
 * Build the editable composer draft for a slide template. This is what gets
 * dropped into the composer when the user picks a template — NOT auto-sent.
 * Pure + deterministic so it can be unit-tested and reused by the SPA picker.
 *
 * The draft = the intro instruction, the numbered outline (which fixes the
 * slide count), and the style directive — everything the slideshow flow needs
 * to start, with {placeholders} left for the user to fill.
 */
export function slideTemplateDraft(t: SlideTemplate): string {
  const outline = t.outline.map((section, i) => `${i + 1}. ${section}`).join('\n');
  return [
    t.promptIntro,
    '',
    `Deck outline (${t.outline.length} slides):`,
    outline,
    '',
    `Style: ${t.style}`,
  ].join('\n');
}
